import FuzzySearch from "fuzzy-search"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import styled, { css, useTheme } from "styled-components"
import { Icon, type IconProps } from "../Icon"
import { Loader } from "../Loader"
import type { CSSCursor, CSSTextAlign } from "../types/css"
import { ZIndexRange } from "../types/enums"
import type { AutoCompleteType, HTMLInputMode } from "../types/html"
import type { BaseProps, BorderProps } from "../types/props"
import { ComponentError } from "../util/components"
import { useClickOutside } from "../util/hooks"
import { getBorderStyle } from "../util/props/border"
import { contrastColor, overloadColor } from "../util/style"
import { isFunction, isUndefined } from "../util/typechecks"

interface WrapperProps {
  disabled: boolean
  inline?: boolean

  width: string
  height: string
}

const Wrapper = styled.div<WrapperProps>`
  position: relative;
  display: ${(props) => (props.inline ? "inline-flex" : "flex")};
  width: ${(props) => props.width};
  height: ${(props) => props.height};
`

interface StyledInputProps extends BorderProps {
  color: string
  background: string
  fontSize: string
  textAlign: CSSTextAlign
  borderRadius: string
  focusBorderColor: string
  iconSide: IconPosition | "both" | "none"
  autoCompleteOpen: boolean
}

const StyledInput = styled.input<StyledInputProps>`
  width: 100%;
  padding: 6px;

  color: ${(props) => overloadColor(props.color)};
  background: ${(props) => overloadColor(props.background)};
  font-size: ${(props) => props.fontSize};
  text-align: ${(props) => props.textAlign};

  ${getBorderStyle};

  border-radius: ${(props) => {
    const open = props.autoCompleteOpen
    const radius = props.borderRadius

    switch (props.iconSide) {
      case "left":
        return `0 ${radius} ${open ? "0" : radius} 0;`
      case "right":
        return `${radius} 0 0 ${open ? "0" : radius};`
      case "both":
        return "0;"
      default:
        return open ? `${radius} ${radius} 0 0;` : `${radius};`
    }
  }};

  transition: border-color 0.1s linear;

  &:focus {
    outline: 0;
    border-color: ${(props) => overloadColor(props.focusBorderColor)};
  }
`
const ICON_WIDTH = 42

interface IconWrapperProps extends BorderProps {
  position: IconPosition
  borderRadius: string
  background: string
  hoverBackground?: string
  hoverIconColor?: string
  cursor?: CSSCursor
}

const IconWrapper = styled.div<IconWrapperProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-width: ${ICON_WIDTH}px;
  width: ${ICON_WIDTH}px;
  max-width: ${ICON_WIDTH}px;
  background: ${(props) => overloadColor(props.background)};

  ${getBorderStyle};

  border-radius: ${(props) =>
    props.position === "left"
      ? `${props.borderRadius} 0 0 ${props.borderRadius}`
      : `0 ${props.borderRadius} ${props.borderRadius} 0`};
  border-width: ${(props) =>
    props.position === "left" ? "1px 0 1px 1px" : "1px 1px 1px 0"};

  color: ${(props) =>
    contrastColor(overloadColor(props.background), "#eef", "#557", "#557")};

  cursor: ${(props) => props.cursor};

  &:hover {
    background: ${(props) => overloadColor(props.hoverBackground)};

    ${(props) =>
      props.hoverIconColor &&
      css`
        i {
          color: ${overloadColor(props.hoverIconColor)} !important;
        }
      `};
  }
`
const ResetIcon = styled(Icon)`
  position: absolute;
  display: flex;
  align-items: center;
  top: 1px;
  right: 16px;
  height: 100%;
  transition: color 0.2s linear;
`
const Loading = styled.div`
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
`

interface AutoCompleteWrapperProps {
  leftIcon: boolean
  rightIcon: boolean

  color: string
  background: string
}

const AutoCompleteWrapper = styled.div<AutoCompleteWrapperProps>`
  position: absolute;
  top: 100%;
  z-index: ${ZIndexRange.Dropdowns};
  max-height: 250px;
  overflow: hidden;

  ${(props) => {
    const nIcons = +props.leftIcon + +props.rightIcon
    const width = `calc(100% - (${nIcons} * ${ICON_WIDTH}px))`
    const left = props.leftIcon ? `${ICON_WIDTH}px` : "0"

    return `
      left: ${left};
      width: ${width};
    `
  }}

  color: ${(props) => props.color};
  background-color: ${(props) => props.background};
  border: solid ${(props) => props.theme.colors.border.default};
  border-width: 0 1px 1px 1px;

  border-top-left-radius: 0;
  border-top-left-radius: 0;
  border-top-left-radius: 0;
  border-top-left-radius: 0;

  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: ${(props) => props.theme.sizes.defaultBorderRadius};
  border-bottom-left-radius: ${(props) => props.theme.sizes.defaultBorderRadius};

  box-shadow: 0 4px 4px -4px rgba(0, 0, 0, 0.2);

  div.--input-autocomplete-inner {
    width: 100%;
    max-height: 250px;
    overflow-y: auto;
  }
`

interface AutoCompleteItem {
  active: boolean
}

const AutoCompleteItem = styled.div<AutoCompleteItem>`
  padding: 4px;
  font-size: 0.8em;
  user-select: none;
  text-align: left;

  &:hover {
    background-color: ${(props) => props.theme.colors.veryLightBlue};

    &::before {
      content: '\uf0da';
      font-family: 'Font Awesome 5 Pro';
      font-size: 0.8rem;
      font-weight: 900;
      margin-right: 2px;
      transform: translateY(1px);
    }
  }

  ${(props) =>
    props.active &&
    css`
      background-color: ${props.theme.colors.veryLightBlue};

      &::before {
        content: '\uf0da';
        font-family: 'Font Awesome 5 Pro';
        font-size: 0.8rem;
        font-weight: 900;
        margin-right: 2px;
        transform: translateY(1px);
      }
    `}
`

const Label = styled.label`
  position: absolute;
  place-self: center;
  right: 10px;
  color: ${({ theme }) => theme.colors.gray5};
`

type IconPosition = "left" | "right"

interface ExtendedIconProps extends IconProps {
  boxBackground?: string
  boxHoverBackground?: string
  boxHoverIconColor?: string
  boxCursor?: CSSCursor
}

type InputType =
  | "button"
  | "checkbox"
  | "color"
  | "date"
  | "datetime-local"
  | "email"
  | "file"
  | "hidden"
  | "image"
  | "month"
  | "number"
  | "password"
  | "radio"
  | "range"
  | "reset"
  | "search"
  | "submit"
  | "tel"
  | "text"
  | "time"
  | "url"
  | "week"

export interface InputProps extends BorderProps, BaseProps {
  // Functional
  value: string
  name?: string
  error?: string | null
  errorTimeout?: number
  errorOffsetX?: string
  errorOffsetY?: string
  hideErrorMessage?: boolean
  type?: InputType

  placeholder?: string
  rightLabel?: string

  disabled?: boolean
  autoFocus?: boolean
  autoComplete?: AutoCompleteType
  tabIndex?: number
  inputMode?: HTMLInputMode
  maxLength?: number

  iconLeftProps?: ExtendedIconProps
  iconRightProps?: ExtendedIconProps

  /** Either a string array or a function returning a string array denoting elements to be shown in autocomplete dropdown */
  autoCompleteSource?: string[] | ((query: string) => string[])
  /** Whether to show autocomplete dropdown when input gains focus  */
  runAutoCompleteOnFocus?: boolean
  /** Whether to run autocomplete also when there's no text in input  */
  runAutoCompleteWithNoInput?: boolean
  /** Select text in input when input gains focus */
  selectOnFocus?: boolean
  /** Display a button to empty input content */
  resetButton?: boolean
  /** Display loader in input */
  loading?: boolean

  // Style props

  inline?: boolean
  width?: string
  fullWidth?: boolean
  height?: string

  color?: string
  background?: string
  fontSize?: string
  textAlign?: CSSTextAlign
  borderRadius?: string

  focusBorderColor?: string

  // Events

  onErrorTimeout?(): void
  onClick?(event: React.MouseEvent<HTMLDivElement>): void
  onKeyDown?(event: React.KeyboardEvent<HTMLInputElement>): void
  onEnter?(event: React.KeyboardEvent<HTMLInputElement>): void
  onWheel?(event: React.WheelEvent<HTMLInputElement>): void
  onFocus?(event: React.FocusEvent<HTMLInputElement>): void
  onBlur?(event: React.FocusEvent<HTMLInputElement>): void
  /**
   * Invoked when an auto complete icon is selected, either by mouse click or enter.
   * Use event.preventDefault to prevent the item from being selected.
   */
  onAutoCompleteItemSelect?(
    item: string,
    event:
      | React.MouseEvent<HTMLDivElement>
      | React.KeyboardEvent<HTMLInputElement>,
  ): void
  onChange(value: string, evt?: React.ChangeEvent<HTMLInputElement>): void
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      id,

      value,
      name,
      error,
      errorTimeout,
      errorOffsetX = "0px",
      errorOffsetY = "-2px",
      hideErrorMessage = false,
      type,

      placeholder,
      rightLabel,
      autoFocus = false,
      disabled = false,
      autoComplete,
      tabIndex = 0,
      inputMode,
      maxLength,

      iconLeftProps,
      iconRightProps,

      autoCompleteSource,
      runAutoCompleteOnFocus = false,
      runAutoCompleteWithNoInput = false,
      selectOnFocus = false,
      resetButton = false,
      loading = false,

      inline = true,
      width = "auto",
      fullWidth = false,
      height = "32px",
      color = "text.dark",
      background = "white",
      fontSize = "1em",
      textAlign = "left",

      border,
      borderWidth,
      borderStyle,
      borderColor,
      borderRadius,
      focusBorderColor = "primary",

      onErrorTimeout = () => void 0,
      onClick = () => void 0,
      onKeyDown = () => void 0,
      onEnter = () => void 0,
      onWheel = () => void 0,
      onFocus = () => void 0,
      onBlur = () => void 0,
      onAutoCompleteItemSelect,
      onChange,
    },
    ref,
  ) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const theme = useTheme()

    const [autoCompleteItems, setAutoCompleteItems] = useState<string[]>([])
    const [autoCompleteSelectedIndex, setAutoCompleteSelectedIndex] =
      useState(-1)
    const [internalError, setInternalError] = useState(error)

    useEffect(() => {
      setInternalError(error)
    }, [error])

    useEffect(() => {
      setAutoCompleteSelectedIndex(-1)
    }, [autoCompleteItems])

    useClickOutside(wrapperRef, () => setAutoCompleteItems([]))

    const iconSide = useMemo(() => {
      if (isUndefined(iconLeftProps) && isUndefined(iconRightProps))
        return "none"
      else if (isUndefined(iconLeftProps) && !isUndefined(iconRightProps))
        return "right"
      else if (!isUndefined(iconLeftProps) && isUndefined(iconRightProps))
        return "left"
      else return "both"
    }, [iconLeftProps, iconRightProps])

    const parsedBorderRadius = borderRadius ?? theme.sizes.defaultBorderRadius

    const cycleAutoComplete = useCallback(
      (delta: number) => {
        const clamp = (n: number) =>
          Math.max(-1, Math.min(n, autoCompleteItems.length))
        setAutoCompleteSelectedIndex(() =>
          clamp(autoCompleteSelectedIndex + delta),
        )
      },
      [autoCompleteItems.length, autoCompleteSelectedIndex],
    )

    const updateAutoComplete = useCallback(
      (query: string) => {
        if (
          isUndefined(autoCompleteSource) ||
          (!runAutoCompleteWithNoInput && query === "")
        ) {
          setAutoCompleteItems(() => [])
          return
        }

        if (isFunction(autoCompleteSource))
          setAutoCompleteItems(() => autoCompleteSource(query))
        else {
          const searcher = new FuzzySearch(autoCompleteSource, undefined, {
            sort: true,
          })

          setAutoCompleteItems(() => searcher.search(query))
        }
      },
      [autoCompleteSource, runAutoCompleteWithNoInput],
    )
    const handleAutoCompleteItemClick = useCallback(
      (item: string, evt: React.MouseEvent<HTMLDivElement>) => {
        onAutoCompleteItemSelect?.(item, evt)
        if (evt.defaultPrevented) return

        onChange(item)
        setAutoCompleteItems(() => [])
      },
      [onAutoCompleteItemSelect, onChange],
    )

    const overrideIconClick = useCallback(
      (position: IconPosition, evt: React.MouseEvent<HTMLElement>) => {
        if (
          position === "left" &&
          !isUndefined(iconLeftProps) &&
          isFunction(iconLeftProps.onClick)
        )
          iconLeftProps.onClick(evt)
        else if (
          position === "right" &&
          !isUndefined(iconRightProps) &&
          isFunction(iconRightProps.onClick)
        )
          iconRightProps.onClick(evt)
      },
      [iconLeftProps, iconRightProps],
    )

    const handleKeyDown = useCallback(
      (evt: React.KeyboardEvent<HTMLInputElement>) => {
        onKeyDown(evt)
        if (evt.defaultPrevented) return

        if (!isUndefined(autoCompleteSource)) {
          switch (evt.key) {
            case "ArrowDown":
              evt.preventDefault()
              cycleAutoComplete(1)
              break
            case "ArrowUp":
              evt.preventDefault()
              cycleAutoComplete(-1)
              break
            case "Enter":
              onEnter(evt)
              if (evt.defaultPrevented) return

              if (
                autoCompleteSelectedIndex > -1 &&
                autoCompleteSelectedIndex < autoCompleteItems.length
              ) {
                const item = autoCompleteItems[autoCompleteSelectedIndex]
                onAutoCompleteItemSelect?.(item, evt)
                if (evt.defaultPrevented) return

                evt.preventDefault()
                onChange(autoCompleteItems[autoCompleteSelectedIndex])
                setAutoCompleteItems(() => [])
              }
              break
          }
        } else if (evt.key === "Enter") {
          onEnter(evt)
        }
      },
      [
        autoCompleteItems,
        autoCompleteSelectedIndex,
        autoCompleteSource,
        cycleAutoComplete,
        onAutoCompleteItemSelect,
        onChange,
        onEnter,
        onKeyDown,
      ],
    )

    function handleErrorTimeout() {
      setInternalError(null)
      onErrorTimeout()
    }

    function handleChange(value: string, doAutoComplete?: boolean): void
    function handleChange(
      evt: React.ChangeEvent<HTMLInputElement>,
      doAutoComplete?: boolean,
    ): void
    function handleChange(
      arg: string | React.ChangeEvent<HTMLInputElement>,
      doAutoComplete = true,
    ) {
      const val = typeof arg === "string" ? arg : arg.target.value

      if (doAutoComplete) updateAutoComplete(val)

      onChange(val, typeof arg === "string" ? undefined : arg)
    }

    function handleFocus(evt: React.FocusEvent<HTMLInputElement>) {
      onFocus(evt)
      if (evt.defaultPrevented) return

      if (runAutoCompleteOnFocus) updateAutoComplete(value)

      if (selectOnFocus) evt.target.select()
    }

    const parsedBorder = useMemo(
      () => border ?? theme.layout.defaultBorder,
      [border, theme.layout.defaultBorder],
    )

    return (
      <Wrapper
        ref={wrapperRef}
        className={className}
        id={id}
        disabled={disabled}
        inline={inline}
        width={fullWidth ? "100%" : width}
        height={height}
        onClick={onClick}
      >
        <ComponentError
          className="--input-error"
          error={internalError}
          hide={hideErrorMessage}
          offsetX={errorOffsetX}
          offsetY={errorOffsetY}
          timeout={errorTimeout}
          onTimeout={handleErrorTimeout}
        />

        {iconLeftProps && (
          <IconWrapper
            className="--input-icon-left"
            position="left"
            color={color}
            background={iconLeftProps.boxBackground ?? background}
            hoverBackground={iconLeftProps.boxHoverBackground}
            hoverIconColor={iconLeftProps.boxHoverIconColor}
            cursor={iconLeftProps.boxCursor}
            border={parsedBorder}
            borderWidth={borderWidth}
            borderStyle={borderStyle}
            borderColor={borderColor}
            borderRadius={parsedBorderRadius}
            onClick={(e) => overrideIconClick("left", e)}
          >
            <Icon {...iconLeftProps} onClick={undefined} />
          </IconWrapper>
        )}

        <StyledInput
          ref={ref}
          className="--input-input"
          value={value}
          name={name}
          type={type}
          autoFocus={autoFocus}
          inputMode={inputMode}
          tabIndex={tabIndex}
          disabled={disabled}
          autoComplete={autoComplete}
          maxLength={maxLength}
          placeholder={placeholder}
          color={color}
          background={background}
          fontSize={fontSize}
          textAlign={textAlign}
          border={parsedBorder}
          borderWidth={borderWidth}
          borderStyle={borderStyle}
          borderColor={borderColor}
          borderRadius={parsedBorderRadius}
          focusBorderColor={focusBorderColor}
          iconSide={iconSide}
          autoCompleteOpen={!!autoCompleteItems.length}
          onKeyDown={handleKeyDown}
          onWheel={onWheel}
          onFocus={handleFocus}
          onBlur={onBlur}
          onChange={handleChange}
        />

        {rightLabel && <Label>{rightLabel}</Label>}

        {iconRightProps && (
          <IconWrapper
            className="--input-icon-right"
            position="right"
            color={color}
            background={iconRightProps.boxBackground ?? background}
            hoverBackground={iconRightProps.boxHoverBackground}
            hoverIconColor={iconRightProps.boxHoverIconColor}
            cursor={iconRightProps.boxCursor}
            border={parsedBorder}
            borderWidth={borderWidth}
            borderStyle={borderStyle}
            borderColor={borderColor}
            borderRadius={parsedBorderRadius}
            onClick={(e) => overrideIconClick("right", e)}
          >
            <Icon {...iconRightProps} onClick={undefined} />
          </IconWrapper>
        )}

        {resetButton && (
          <ResetIcon
            className="--input-reset-icon"
            icon="times"
            color={value === "" ? "gray6" : "primary"}
            onClick={() => handleChange("")}
          />
        )}

        {loading && (
          <Loading>
            <Loader.Spinner size={20} thickness={2} />
          </Loading>
        )}

        {autoCompleteSource && !!autoCompleteItems.length && (
          <AutoCompleteWrapper
            className="--input-autocomplete"
            leftIcon={!!iconLeftProps}
            rightIcon={!!iconRightProps}
            color={color}
            background={background}
          >
            <div className="--input-autocomplete-inner">
              {autoCompleteItems.map((item, index) => (
                <AutoCompleteItem
                  key={index + item}
                  className="--input-autocomplete-item"
                  active={autoCompleteSelectedIndex === index}
                  onClick={(evt) => handleAutoCompleteItemClick(item, evt)}
                >
                  {item}
                </AutoCompleteItem>
              ))}
            </div>
          </AutoCompleteWrapper>
        )}
      </Wrapper>
    )
  },
)
